import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { selectUser } from "redux/authReducer";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";

import {
  PRIVATE_DATASETS_PATH,
  PUBLIC_DATASETS_PATH,
} from "helpers/constants/paths";
import { initAmplitude } from "helpers/utility/amplitude";

import Layout from "views/layout";
import Login from "views/pages/Login";
import DatasetInventory from "views/pages/DatasetInventory";
import DataPage from "views/pages/DataPage";
import MobileScreenErrorModal from "views/modals/MobileScreenErrorModal";
import DatasetCreationPage from "views/pages/DatasetCreationPage";

import "./App.scss";
import { fetchUserInfo } from "redux/User/actions";
import { isFastdupUser, isOnPremUser } from "helpers/utility/utilities";
import ErrorComponent from "views/layout/ErrorComponent";
import EnrichmentPage from "views/pages/EnrichmentPage";
import SavedViewsPage from "views/pages/SavedViewsPage";
import TaskManagerPage from "views/pages/TaskManager";
import LoginLayout from "views/components/LoginLayout";
import FreeTrialPage from "views/pages/FreeTrialPage";
import ThankYouPage from "views/pages/ThankYouPage";
import BrowserSupportModal from "views/modals/BrowserSupportModal";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "./views/providers/ConfigProvider";
import ModelCatalogPage from "views/pages/ModelCatalog";
import { ThemeProvider } from "./views/providers/ThemeProvider";
import WorkspaceSettingsPage from "views/pages/WorkspaceSettings";
import RuntimeConfigPage from "views/pages/RuntimeConfigPage";
import { DatasetPageWrapper } from "./views/pages/common/DatasetPageWrapper";
import { UserConfigProvider } from "contexts/UserConfigContext";

const LoginLayoutWithProvider = () => (
  <UserConfigProvider>
    <LoginLayout />
  </UserConfigProvider>
);

const LoginRouter = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<LoginLayoutWithProvider />}>
        <Route path="/free-trial" element={<FreeTrialPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route
          path="/login"
          element={<Login />}
          errorElement={<ErrorComponent />}
        />
      </Route>,
    ),
  );
  return <RouterProvider router={router} />;
};

const DataPageRouter = () => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<Layout />} errorElement={<ErrorComponent />}>
        <Route
          path="/dataset/:datasetId/data"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/dataset/:datasetId/data/cluster/:clusterId"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/dataset/:datasetId/data/image/:imageId"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/dataset/:datasetId/data/cluster/:clusterId/image/:imageId"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
      </Route>,
    ),
  );
};
const DataAndLegacyRouter = () => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<Layout />} errorElement={<ErrorComponent />}>
        <Route
          path="/"
          element={<DatasetInventory />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path={PRIVATE_DATASETS_PATH}
          element={<DatasetInventory />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path={PUBLIC_DATASETS_PATH}
          element={<DatasetInventory />}
          errorElement={<ErrorComponent />}
        />
        <Route path="/dataset" element={<DatasetPageWrapper />}>
          <Route path="/dataset/:datasetId/data" element={<DataPage />} />
          <Route
            path="/dataset/:datasetId/data/seed/:seedId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:datasetId/data/cluster/:clusterId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:datasetId/data/image/:imageId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:datasetId/data/cluster/:clusterId/image/:imageId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:newDatasetId/create"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update/:batchId"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:enrichDatasetId/enrich"
            element={<EnrichmentPage />}
          />
          <Route
            path="/dataset/:viewsDatasetId/views"
            element={<SavedViewsPage />}
          />
          <Route
            path="/dataset/:newDatasetId/create/on-prem"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update/on-prem"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update/:batchId/on-prem"
            element={<DatasetCreationPage />}
          />
        </Route>
        <Route
          path="/task-manager"
          element={<TaskManagerPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/models-catalog"
          element={<ModelCatalogPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/workspace-settings"
          element={<WorkspaceSettingsPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/runtime-config"
          element={<RuntimeConfigPage />}
          errorElement={<ErrorComponent />}
        />
      </Route>,
    ),
  );
};
const WrappedRoutes = () => {
  return (
    <RouterProvider
      router={isFastdupUser() ? DataPageRouter() : DataAndLegacyRouter()}
    />
  );
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});

const RouterApp = () => {
  const dispatch = useDispatch();
  const pathname = window.location.pathname.split("/")[1];
  const isLoginRoute = ["free-trial", "thank-you", "login"].includes(pathname);
  const [user, setUser] = useState(selectUser());

  useEffect(() => {
    const currentUser = selectUser();
    if (!user) {
      setUser(currentUser);
    }
    //eslint-disable-next-line
  }, []);

  // Redirect unauthenticated users to OIDC login when auth is enabled
  useEffect(() => {
    const authEnabled =
      process.env.REACT_APP_IS_AUTHENTICATION_DISABELED !== "true";
    if (authEnabled && !user && !isLoginRoute && isOnPremUser()) {
      window.location.href = "/api/v1/oidc/login";
    }
  }, [user, isLoginRoute]);

  useEffect(() => {
    if (user) {
      dispatch(fetchUserInfo(user) as any);
    }
  }, [user, dispatch]);

  initAmplitude();

  return (
    <ThemeProvider>
      <ConfigProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserSupportModal />
          <MobileScreenErrorModal />
          {isLoginRoute ? <LoginRouter /> : <WrappedRoutes />}
        </QueryClientProvider>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default RouterApp;
